// App.jsx - WITH ALL ROUTES ✅
import { useState, useEffect, useRef } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  useNavigate
} from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "./components/Navbar";
import Hero from "./components/Hero";
import Services from "./components/Services";
import Prices from "./components/Prices";
import Contact from "./components/Contact";
import ContactPage from "./pages/Contact";
import FAQsPage from "./pages/FAQs";
import AboutPage from "./pages/About";
import RefundPolicy from "./pages/RefundPolicy";
import TermsAndConditions from "./pages/TermsAndConditions";
import Footer from "./components/Footer";
import EnquiryPopup from "./components/EnquiryPopup";
import WhatsAppButton from "./components/WhatsAppButton";
import AdminPanel from "./pages/AdminPanel";
import {
  isRenderPrepMode,
  useRenderModePreparation,
} from "./utils/screenshotMode";

// Solutions - By Team
import AgencyTeams from "./pages/solutions/AgencyTeams";
import PerformanceTeams from "./pages/solutions/PerformanceTeams";
import BrandTeams from "./pages/solutions/BrandTeams";
import CreativeTeams from "./pages/solutions/CreativeTeams";

// Solutions - By Channel
import SocialMedia from "./pages/SocialMedia";
import ConversationalCommerce from "./pages/ConversationalCommerce";
import ConnectedTV from "./pages/ConnectedTV";
import OpenWeb from "./pages/OpenWeb";
import Video from "./pages/Video";

// Marketing Resources Pages
import ResourceLibrary from "./pages/resources/ResourceLibrary";
import AdvertisingGuide from "./pages/resources/AdvertisingGuide";
import Reports from "./pages/resources/Reports";
import Stories from "./pages/resources/Stories";
import Blog from "./pages/resources/Blog";
import Events from "./pages/resources/Events";
import News from "./pages/resources/News";

// Free Tools Pages
import ROASCalculator from "./pages/tools/ROASCalculator";
import UTMBuilder from "./pages/tools/UTMBuilder";
import ProfitCalculator from "./pages/tools/ProfitCalculator";

// Customer Support Pages
import GettingStarted from "./pages/support/GettingStarted";
import HelpCenter from "./pages/support/HelpCenter";

// Platform data with brand colors
const platforms = [
  {
    name: "Facebook Ads", color: "#1877F2", desc: "Reach 2.9B users with advanced targeting",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" /></svg>, cat: "social"
  },
  {
    name: "Instagram Ads", color: "#E4405F", desc: "Visual storytelling for 2B+ users",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98C.014 8.332 0 8.74 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.332 23.986 8.74 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0z" /></svg>, cat: "social"
  },
  {
    name: "TikTok Ads", color: "#000000", desc: "Viral short-form video advertising",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z" /></svg>, cat: "social"
  },
  {
    name: "LinkedIn Ads", color: "#0A66C2", desc: "B2B professional network advertising",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 1 1 0-4.125 2.062 2.062 0 0 1 0 4.125zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" /></svg>, cat: "social"
  },
  {
    name: "Twitter/X Ads", color: "#000000", desc: "Real-time conversation marketing",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" /></svg>, cat: "social"
  },
  {
    name: "Snapchat Ads", color: "#FFFC00", desc: "AR experiences for Gen Z audience",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12.206.793c.99 0 4.347.276 5.93 3.821.529 1.193.403 3.219.299 4.847l-.003.06c-.012.18-.022.345-.03.51.075.045.203.09.401.09.3-.016.659-.12 1.033-.301a.76.76 0 0 1 .464-.104c.182 0 .359.029.509.09.45.149.734.479.734.838.015.449-.39.839-1.213 1.168-.089.029-.209.075-.344.119-.45.135-1.139.36-1.333.81-.09.224-.061.524.12.868l.015.015c.06.136 1.526 3.475 4.791 4.014.255.044.435.27.42.509 0 .075-.015.149-.045.225-.24.569-1.273.988-3.146 1.271-.059.091-.12.375-.164.57-.029.179-.074.36-.134.553-.076.271-.27.405-.555.405h-.03a3.3 3.3 0 0 1-.538-.074 7.987 7.987 0 0 0-1.273-.135c-.3 0-.599.015-.913.074-.6.104-1.123.464-1.723.884-.853.599-1.826 1.288-3.294 1.288-.06 0-.119-.015-.18-.015h-.149c-1.468 0-2.427-.675-3.279-1.288-.599-.42-1.107-.779-1.707-.884a5.946 5.946 0 0 0-.928-.074c-.54 0-.958.089-1.272.149-.211.043-.391.074-.54.074-.374 0-.523-.224-.583-.42a4.91 4.91 0 0 1-.135-.567c-.046-.181-.105-.494-.166-.57-1.918-.222-2.95-.642-3.189-1.226a.822.822 0 0 1-.055-.225c-.015-.243.165-.465.42-.509 3.264-.54 4.73-3.879 4.791-4.02l.016-.029c.18-.345.224-.645.119-.869-.195-.434-.884-.658-1.332-.809a3.108 3.108 0 0 1-.346-.119c-1.107-.435-1.257-.93-1.197-1.273.09-.479.674-.793 1.168-.793.146 0 .27.029.383.074.42.194.789.3 1.104.3.234 0 .384-.06.465-.105l-.046-.569c-.098-1.626-.225-3.651.307-4.837C7.392 1.077 10.739.807 11.727.807l.419-.015h.06z" /></svg>, cat: "social"
  },
  {
    name: "Pinterest Ads", color: "#BD081C", desc: "Discovery & inspiration advertising",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 5.079 3.158 9.417 7.618 11.162-.105-.949-.199-2.403.041-3.439.219-.937 1.406-5.957 1.406-5.957s-.359-.72-.359-1.781c0-1.663.967-2.911 2.168-2.911 1.024 0 1.518.769 1.518 1.688 0 1.029-.653 2.567-.992 3.992-.285 1.193.6 2.165 1.775 2.165 2.128 0 3.768-2.245 3.768-5.487 0-2.861-2.063-4.869-5.008-4.869-3.41 0-5.409 2.562-5.409 5.199 0 1.033.394 2.143.889 2.741.099.12.112.225.085.345-.09.375-.293 1.199-.334 1.363-.053.225-.172.271-.401.165-1.495-.69-2.433-2.878-2.433-4.646 0-3.776 2.748-7.252 7.92-7.252 4.158 0 7.392 2.967 7.392 6.923 0 4.135-2.607 7.462-6.233 7.462-1.214 0-2.354-.629-2.758-1.379l-.749 2.848c-.269 1.045-1.004 2.352-1.498 3.146 1.123.345 2.306.535 3.55.535 6.607 0 11.985-5.365 11.985-11.987C23.97 5.39 18.592.026 11.985.026L12.017 0z" /></svg>, cat: "social"
  },
  {
    name: "Google Ads", color: "#4285F4", desc: "Search, Display, Shopping & YouTube",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.07 5.07 0 0 1-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" /><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" /><path d="M5.84 14.09A6.97 6.97 0 0 1 5.49 12c0-.72.13-1.43.35-2.09V7.07H2.18A11.01 11.01 0 0 0 1 12c0 1.78.43 3.45 1.18 4.93l3.66-2.84z" /><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" /></svg>, cat: "search"
  },
  {
    name: "Microsoft Ads", color: "#00A4EF", desc: "Bing search and partner networks",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M11.4 24H0V12.6h11.4V24zM24 24H12.6V12.6H24V24zM11.4 11.4H0V0h11.4v11.4zm12.6 0H12.6V0H24v11.4z" /></svg>, cat: "search"
  },
  {
    name: "Amazon Ads", color: "#FF9900", desc: "E-commerce advertising at scale",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M.045 18.02c.072-.116.187-.124.348-.022 3.636 2.11 7.594 3.166 11.87 3.166 2.852 0 5.668-.533 8.447-1.595l.315-.14c.138-.06.234-.1.293-.13.226-.088.39-.046.525.13.12.174.09.336-.12.48a18.65 18.65 0 0 1-1.094.583c-.474.228-1.117.513-1.867.84-1.616.663-3.287 1.177-5.018 1.544-1.73.364-3.48.546-5.245.546-2.898 0-5.594-.605-8.096-1.816l-.047-.024c-.282-.13-.352-.228-.352-.417 0-.12.032-.23.098-.328z" /></svg>, cat: "search"
  },
  {
    name: "YouTube Ads", color: "#FF0000", desc: "Video ads on world's #2 search engine",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z" /></svg>, cat: "search"
  },
  {
    name: "Reddit Ads", color: "#FF4500", desc: "Community-driven advertising",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0A12 12 0 0 0 0 12a12 12 0 0 0 12 12 12 12 0 0 0 12-12A12 12 0 0 0 12 0zm5.01 4.744c.688 0 1.25.561 1.25 1.249a1.25 1.25 0 0 1-2.498.056l-2.597-.547-.8 3.747c1.824.07 3.48.632 4.674 1.488.308-.309.73-.491 1.207-.491.968 0 1.754.786 1.754 1.754 0 .716-.435 1.333-1.01 1.614.028.176.042.352.042.52 0 2.694-3.13 4.87-7.004 4.87-3.874 0-7.004-2.176-7.004-4.87 0-.183.015-.366.043-.534A1.748 1.748 0 0 1 4.028 12c0-.968.786-1.754 1.754-1.754.463 0 .898.196 1.207.49 1.207-.883 2.878-1.43 4.744-1.487l.885-4.182a.342.342 0 0 1 .14-.197.35.35 0 0 1 .238-.042l2.906.617a1.214 1.214 0 0 1 1.108-.701z" /></svg>, cat: "emerging"
  },
  {
    name: "Spotify Ads", color: "#1DB954", desc: "Audio advertising for music lovers",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02z" /></svg>, cat: "emerging"
  },
  {
    name: "Twitch Ads", color: "#9146FF", desc: "Gaming and live streaming ads",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M11.571 4.714h1.715v5.143H11.57zm4.715 0H18v5.143h-1.714zM6 0L1.714 4.286v15.428h5.143V24l4.286-4.286h3.428L22.286 12V0zm14.571 11.143l-3.428 3.428h-3.429l-3 3v-3H6.857V1.714h13.714Z" /></svg>, cat: "emerging"
  },
  {
    name: "Apple Search", color: "#000000", desc: "App Store search advertising",
    icon: <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09l.01-.01zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" /></svg>, cat: "emerging"
  },
];

const categories = [
  { key: "all", label: "All Platforms" },
  { key: "social", label: "Social Media" },
  { key: "search", label: "Search & Display" },
  { key: "emerging", label: "Emerging" },
];

function PlatformsSection({ onOpenEnquiry }) {
  const [activeCat, setActiveCat] = useState("all");
  const navigate = useNavigate();
  const filtered = activeCat === "all" ? platforms : platforms.filter(p => p.cat === activeCat);

  return (
    <section className="bg-white py-16 sm:py-20 lg:py-24 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-[400px] h-[400px] bg-[#00809D]/5 rounded-full -translate-x-1/2 -translate-y-1/2" />
      <div className="absolute bottom-0 right-0 w-[300px] h-[300px] bg-[#00809D]/5 rounded-full translate-x-1/3 translate-y-1/3" />

      <div className="max-w-6xl mx-auto relative z-10">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="text-center mb-12 sm:mb-14">
          <div className="inline-flex items-center gap-2 bg-[#00809D]/10 px-4 py-2 rounded-full mb-5 border border-[#00809D]/20">
            <span className="w-2 h-2 bg-[#00809D] rounded-full animate-pulse" />
            <span className="text-[#00809D] font-semibold text-sm">15+ Platforms</span>
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Advertise on Every <span className="text-[#00809D]">Platform</span>
          </h2>
          <p className="text-gray-500 text-base sm:text-lg max-w-2xl mx-auto">
            We manage campaigns across all major advertising platforms to maximize your reach and ROI.
          </p>
        </motion.div>

        <motion.div initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.1 }} className="flex flex-wrap justify-center gap-2 sm:gap-3 mb-10 sm:mb-12">
          {categories.map(c => (
            <button key={c.key} onClick={() => setActiveCat(c.key)}
              className={`px-4 sm:px-5 py-2.5 rounded-xl text-sm font-semibold transition-all duration-300 ${activeCat === c.key
                ? "bg-[#00809D] text-white shadow-lg shadow-[#00809D]/25"
                : "bg-gray-100 text-gray-600 hover:bg-gray-200"
                }`}>{c.label}</button>
          ))}
        </motion.div>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 sm:gap-5">
          {filtered.map((p, i) => (
            <motion.div key={p.name} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }}
              onClick={onOpenEnquiry}
              className="group bg-white rounded-2xl p-5 border border-gray-100 hover:border-[#00809D]/25 hover:shadow-lg hover:shadow-[#00809D]/8 hover:-translate-y-1 transition-all duration-300 text-center cursor-pointer"
            >
              <div className="w-14 h-14 rounded-2xl mx-auto mb-4 flex items-center justify-center text-white transition-transform duration-300 group-hover:scale-110"
                style={{ backgroundColor: p.color === "#FFFC00" ? "#f5c800" : p.color }}>
                {p.icon}
              </div>
              <h4 className="font-bold text-gray-900 text-sm mb-1.5 group-hover:text-[#00809D] transition-colors">{p.name}</h4>
              <p className="text-gray-400 text-xs leading-relaxed">{p.desc}</p>
            </motion.div>
          ))}
        </div>

        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.5, delay: 0.2 }} className="text-center mt-12 sm:mt-14">
          <button onClick={() => navigate('/contact')}
            className="group bg-[#00809D] hover:bg-[#006d87] text-white px-8 py-4 rounded-xl font-bold text-base shadow-lg shadow-[#00809D]/25 hover:shadow-xl hover:shadow-[#00809D]/35 hover:scale-[1.02] active:scale-[0.98] transition-all duration-300 inline-flex items-center gap-2.5">
            Start Multi-Platform Advertising
            <svg className="w-4 h-4 group-hover:translate-x-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" /></svg>
          </button>
        </motion.div>
      </div>
    </section>
  );
}

function Content() {
  const renderPrepMode = isRenderPrepMode();
  const [isLoading, setIsLoading] = useState(true);
  const [showEnquiry, setShowEnquiry] = useState(false);
  const location = useLocation();
  const isFirstLoad = useRef(true);

  useEffect(() => {
    if (renderPrepMode) {
      setIsLoading(false);
      return undefined;
    }
    setIsLoading(true);
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 2000);
    return () => clearTimeout(timer);
  }, [location, renderPrepMode]);

  useRenderModePreparation(renderPrepMode, location.pathname);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location]);

  // Show enquiry popup on every route change (skip first load)
  useEffect(() => {
    if (renderPrepMode) return undefined;
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }
    // Don't show popup on admin panel
    if (location.pathname.startsWith('/admin')) {
      return;
    }
    // Small delay so the page renders first, then popup appears
    const timer = setTimeout(() => setShowEnquiry(true), 2500);
    return () => clearTimeout(timer);
  }, [location.pathname, renderPrepMode]);

  if (isLoading) {
    return (
      <div className="fixed inset-0 bg-black z-50 flex items-center justify-center overflow-hidden">
        <div className="relative w-48 h-48 lg:w-64 lg:h-64 flex items-center justify-center rounded-3xl shadow-2xl">
          <dotlottie-wc
            src="https://lottie.host/36144088-bed8-4464-a6ca-e25cef205d02/RCZ0pc6qKD.lottie"
            autoplay
            loop
            style={{
              width: '100%',
              height: '100%',
              display: 'block'
            }}
          />
        </div>
        <div className="absolute bottom-8 text-center">
          <p className="text-white/80 text-lg sm:text-xl font-semibold tracking-wide">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans min-h-screen">
      <Navbar onOpenEnquiry={() => setShowEnquiry(true)} />
      <Routes>
        {/* Home Page */}
        <Route path="/" element={
          <>
            <Hero onOpenEnquiry={() => setShowEnquiry(true)} />
            <PlatformsSection onOpenEnquiry={() => setShowEnquiry(true)} />
            <Services />
            <Contact />
          </>
        } />

        {/* Prices Page */}
        <Route path="/plan" element={<Prices />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/faqs" element={<FAQsPage />} />
        <Route path="/about" element={<AboutPage />} />
        <Route path="/refund-policy" element={<RefundPolicy />} />
        <Route path="/terms" element={<TermsAndConditions />} />

        {/* Solutions - By Team Routes */}
        <Route path="/solutions/agency-teams" element={<AgencyTeams />} />
        <Route path="/solutions/performance-teams" element={<PerformanceTeams />} />
        <Route path="/solutions/brand-teams" element={<BrandTeams />} />
        <Route path="/solutions/creative-teams" element={<CreativeTeams />} />

        {/* Solutions - By Channel Routes */}
        <Route path="/social-media" element={<SocialMedia />} />
        <Route path="/conversational-commerce" element={<ConversationalCommerce />} />
        <Route path="/connected-tv" element={<ConnectedTV />} />
        <Route path="/open-web" element={<OpenWeb />} />
        <Route path="/video" element={<Video />} />

        {/* Marketing Resources Routes */}
        <Route path="/resources/library" element={<ResourceLibrary />} />
        <Route path="/resources/advertising-guide" element={<AdvertisingGuide />} />
        <Route path="/resources/reports" element={<Reports />} />
        <Route path="/resources/stories" element={<Stories />} />
        <Route path="/resources/blog" element={<Blog />} />
        <Route path="/resources/events" element={<Events />} />
        <Route path="/resources/news" element={<News />} />

        {/* Free Tools Routes */}
        <Route path="/tools/roas-calculator" element={<ROASCalculator />} />
        <Route path="/tools/utm-builder" element={<UTMBuilder />} />
        <Route path="/tools/profit-calculator" element={<ProfitCalculator />} />

        {/* Customer Support Routes */}
        <Route path="/support/getting-started" element={<GettingStarted />} />
        <Route path="/support/help-center" element={<HelpCenter />} />

        {/* Admin */}
        <Route path="/admin" element={<AdminPanel />} />
      </Routes>
      {!location.pathname.startsWith('/admin') && (
        <>
          <Footer />
          <WhatsAppButton />
          {!renderPrepMode && (
            <EnquiryPopup
              isOpen={showEnquiry}
              onClose={() => setShowEnquiry(false)}
            />
          )}
        </>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <Content />
    </Router>
  );
}

export default App;
